/**
 * Google reCAPTCHA v3: invisible and score-based, no click. One token per message,
 * sent as `challenge_token`; the backend verifies it (app/services/verifier.py).
 */

interface ReCaptchaV3 {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: ReCaptchaV3;
  }
}

// Must match the action the backend accepts.
const ACTION = "chat";
const LOAD_FAILED = "Security check failed to load. Refresh the page.";

export class ReCaptcha {
  private loaded: Promise<ReCaptchaV3> | null = null;

  constructor(private readonly siteKey: string) {}

  /** Load the script once; called when the panel first opens. */
  load(): Promise<ReCaptchaV3> {
    this.loaded ??= new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(this.siteKey)}`;
      script.async = true;
      script.onload = () => {
        const api = window.grecaptcha;
        if (!api) return reject(new Error(LOAD_FAILED));
        api.ready(() => resolve(api));
      };
      script.onerror = () => reject(new Error(LOAD_FAILED));
      document.head.appendChild(script);
    });
    return this.loaded;
  }

  /** Tokens are single-use and expire after two minutes, so one per message. */
  async token(): Promise<string> {
    const api = await this.load();
    return api.execute(this.siteKey, { action: ACTION });
  }
}
